<template>
  <v-combobox
    v-model="model"
    class="price-search"
    :class="{ 'price-search--mobile': isMobile }"
    :items="visibleItems"
    :placeholder="placeholder"
    :menu-props="menuProps"
    prepend-inner-icon="ui-icon-search text-info--text"
    append-icon=""
    clear-icon="ui-icon-close-not-a-circle"
    background-color="bg-gray-40"
    solo
    flat
    clearable
    hide-details
    no-filter
    hide-no-data
    height="56"
    data-qa="lpu_services_search"
    @update:search-input="onSearchInput"
    @click:clear="onClear"
    @focus="onFocus"
  >
    <template #item="{ item, on }">
      <v-list-item
        data-qa="lpu_services_search_item"
        v-on="on"
      >
        <v-list-item-content @click="onSelectClass(String(item))">
          <v-list-item-title>{{ item }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-combobox>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import debounce from 'lodash/debounce'
import useIsMobile from 'src/composables/useIsMobile'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import { MIN_SEARCH_LENGTH } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceToolbar/constants'

type Emits = {
  (_event: 'search:text', _value: string): void
  (_event: 'search:class', _value: string): void
  (_event: 'search:default'): void
}

const props = defineProps<{
  items: string[]
}>()

const emit = defineEmits<Emits>()
const isMobile = useIsMobile()
const model = ref('')
const visibleItems = ref([...props.items])
const wasSelectedItem = ref(false)
const lastQuery = ref('')
const placeholder = tStatic('Найти услугу')
const menuProps = {
  maxHeight: 252,
  offsetOverflow: false,
  rounded: true,
}

watch(() => props.items, items => {
  if (!lastQuery.value) {
    visibleItems.value = [...items]
  }
})

const emitSearchQuery = debounce((searchString: string) => {
  // Выбор пункта тоже триггерит update:search-input — пропускаем один раз.
  if (wasSelectedItem.value) {
    wasSelectedItem.value = false

    return
  }

  const query = searchString.toLowerCase().trim()

  if (lastQuery.value === query) {
    return
  }

  lastQuery.value = query

  if (!query) {
    visibleItems.value = [...props.items]
    emit('search:default')

    return
  }

  // Пока набирают текст — прячем список категорий.
  visibleItems.value = []

  if (query.length < MIN_SEARCH_LENGTH) {
    return
  }

  emit('search:text', query)
}, 300)

function onSearchInput(value?: string) {
  emitSearchQuery(value || '')
}

function onClear() {
  visibleItems.value = [...props.items]
  lastQuery.value = ''
  emit('search:default')
}

function onFocus() {
  visibleItems.value = [...props.items]
}

function onSelectClass(name: string) {
  wasSelectedItem.value = true
  lastQuery.value = name.toLowerCase().trim()
  emit('search:class', name)
}
</script>

<style scoped lang="scss">
@import 'src/styles/variables';

.price-search {
  :deep(.v-input__slot) {
    border-radius: 15px;
    min-height: 56px;
  }

  :deep(.v-input__prepend-inner) {
    margin-right: 8px;
    align-self: center;
    padding: 0;
  }

  :deep(.v-icon) {
    color: $ui-kit-text-info;
  }

  :deep(input)::placeholder {
    color: $ui-kit-text-info;
  }
}

.price-search--mobile {
  :deep(.v-input__slot) {
    border-radius: $border-radius-sm;
  }
}
</style>
